import { Avatar, FileButton, Button, Group, Stack, Text, Image, Box } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconMessage, IconPhoto } from "@tabler/icons-react";
import {
    usePostApiBotProviderUploadUploadAvatarIcon,
    useDeleteApiBotProviderUploadDeleteAvatarIcon,
    usePostApiBotProviderUploadUploadWidgetIcon,
    useDeleteApiBotProviderUploadDeleteWidgetIcon,
    usePostApiBotProviderUploadUploadBackgroundImage,
    useDeleteApiBotProviderUploadDeleteBackgroundImage,
    useGetApiBotProviderGet
} from "@/API";
import { GetWebBotAvatarUrl, GetWebBotWidgetUrl, GetWebBotBackgroundImageUrl } from "@/APIURL";
import { useQueryClient } from "@tanstack/react-query";
import { BOT_PROVIDER_METADATA_KEYS } from "./constants";

interface WebBotAvatarUploadProps {
    workspaceId: string;
    botProviderId: string;
}

export function WebBotAvatarUpload({
    workspaceId,
    botProviderId
}: WebBotAvatarUploadProps) {
    const queryClient = useQueryClient();
    const botProviderQuery = useGetApiBotProviderGet({
        workspaceId,
        id: botProviderId
    });
    const botProvider = botProviderQuery.data?.data;
    const value = botProvider?.metadata?.[BOT_PROVIDER_METADATA_KEYS.avatarIconName];

    const uploadMutation = usePostApiBotProviderUploadUploadAvatarIcon();
    const deleteMutation = useDeleteApiBotProviderUploadDeleteAvatarIcon();

    const handleUpload = async (file: File | null) => {
        if (!file) return;

        try {
            await uploadMutation.mutateAsync({
                params: {
                    workspaceId,
                    botProviderId,
                },
                data: {
                    file: file,
                }
            });

            queryClient.invalidateQueries({
                queryKey: botProviderQuery.queryKey
            });
            notifications.show({
                title: "上傳成功",
                message: "機器人頭像已更新",
                color: "teal",
            });
        } catch (error) {
            notifications.show({
                title: "上傳失敗",
                message: "無法上傳頭像，請稍後再試",
                color: "red",
            });
        }
    };

    const handleDelete = () => {
        modals.openConfirmModal({
            title: "確認清除頭像",
            centered: true,
            children: (
                <Text size="sm">
                    您確定要清除目前的機器人頭像嗎？此動作無法復原。
                </Text>
            ),
            labels: { confirm: "確認清除", cancel: "取消" },
            confirmProps: { color: "red" },
            onConfirm: async () => {
                try {
                    await deleteMutation.mutateAsync({
                        params: {
                            workspaceId,
                            botProviderId,
                        }
                    });
                    queryClient.invalidateQueries({
                        queryKey: botProviderQuery.queryKey
                    });
                    notifications.show({
                        title: "刪除成功",
                        message: "機器人頭像已清除",
                        color: "teal",
                    });
                } catch (error) {
                    notifications.show({
                        title: "刪除失敗",
                        message: "無法清除頭像，請稍後再試",
                        color: "red",
                    });
                }
            }
        });
    };

    const imageUrl = value ? GetWebBotAvatarUrl(botProviderId) : undefined;

    return (
        <Stack gap="xs">
            <span style={{ fontSize: '14px', fontWeight: 500 }}>機器人頭像</span>
            <div style={{ fontSize: '12px', color: 'gray' }}>顯示在聊天標題左側及 AI 訊息旁邊</div>
            <Group align="center" gap="lg">
                <Avatar
                    key={value}
                    src={imageUrl}
                    size="lg"
                    radius="xl"
                />
                <Stack gap={4} style={{ flex: 1 }}>
                    <Group gap="xs">
                        <FileButton
                            accept="image/*"
                            onChange={handleUpload}
                            disabled={uploadMutation.isPending}
                        >
                            {(props) => (
                                <Button
                                    {...props}
                                    variant="light"
                                    size="sm"
                                    loading={uploadMutation.isPending}
                                >
                                    {value ? "變更圖片" : "上傳圖片"}
                                </Button>
                            )}
                        </FileButton>
                        {value && (
                            <Button
                                variant="outline"
                                color="red"
                                size="sm"
                                onClick={handleDelete}
                                loading={deleteMutation.isPending}
                                disabled={uploadMutation.isPending}
                            >
                                清除頭像
                            </Button>
                        )}
                    </Group>
                </Stack>
            </Group>
        </Stack>
    );
}

interface WebBotWidgetIconUploadProps {
    workspaceId: string;
    botProviderId: string;
    shape?: string;
}

export function WebBotWidgetIconUpload({
    workspaceId,
    botProviderId,
    shape = "circle"
}: WebBotWidgetIconUploadProps) {
    const queryClient = useQueryClient();
    const botProviderQuery = useGetApiBotProviderGet({
        workspaceId,
        id: botProviderId
    });
    const botProvider = botProviderQuery.data?.data;
    const value = botProvider?.metadata?.[BOT_PROVIDER_METADATA_KEYS.widgetIconName];

    const uploadMutation = usePostApiBotProviderUploadUploadWidgetIcon();
    const deleteMutation = useDeleteApiBotProviderUploadDeleteWidgetIcon();

    const handleUpload = async (file: File | null) => {
        if (!file) return;

        try {
            await uploadMutation.mutateAsync({
                params: {
                    workspaceId,
                    botProviderId,
                },
                data: {
                    file: file,
                }
            });

            queryClient.invalidateQueries({
                queryKey: botProviderQuery.queryKey
            });
            notifications.show({
                title: "上傳成功",
                message: "聊天室開關圖示已更新",
                color: "teal",
            });
        } catch (error) {
            notifications.show({
                title: "上傳失敗",
                message: "無法上傳開關圖示，請稍後再試",
                color: "red",
            });
        }
    };

    const handleDelete = () => {
        modals.openConfirmModal({
            title: "確認清除圖示",
            centered: true,
            children: (
                <Text size="sm">
                    您確定要清除目前的聊天室開關圖示嗎？此動作無法復原。
                </Text>
            ),
            labels: { confirm: "確認清除", cancel: "取消" },
            confirmProps: { color: "red" },
            onConfirm: async () => {
                try {
                    await deleteMutation.mutateAsync({
                        params: {
                            workspaceId,
                            botProviderId,
                        }
                    });
                    queryClient.invalidateQueries({
                        queryKey: botProviderQuery.queryKey
                    });
                    notifications.show({
                        title: "刪除成功",
                        message: "聊天室開關圖示已清除",
                        color: "teal",
                    });
                } catch (error) {
                    notifications.show({
                        title: "刪除失敗",
                        message: "無法清除開關圖示，請稍後再試",
                        color: "red",
                    });
                }
            }
        });
    };

    const imageUrl = value ? GetWebBotWidgetUrl(botProviderId) : undefined;

    return (
        <Stack gap="xs">
            <span style={{ fontSize: '14px', fontWeight: 500 }}>聊天室開關圖示</span>
            <div style={{ fontSize: '12px', color: 'gray' }}>顯示在網頁右下角用於打開聊天室的按鈕圖示</div>
            <Group align="center" gap="lg">
                <Avatar
                    key={`${value}_${shape}`}
                    src={imageUrl}
                    size="lg"
                    radius={shape?.toString().toLowerCase() !== "original" ? "xl" : 0}
                    imageProps={{ style: { objectFit: shape?.toString().toLowerCase() !== "original" ? 'cover' : 'contain' } }}
                >
                    <IconMessage size={35} />
                </Avatar>
                <Stack gap={4} style={{ flex: 1 }}>
                    <Group gap="xs">
                        <FileButton
                            accept="image/*"
                            onChange={handleUpload}
                            disabled={uploadMutation.isPending}
                        >
                            {(props) => (
                                <Button
                                    {...props}
                                    variant="light"
                                    size="sm"
                                    loading={uploadMutation.isPending}
                                >
                                    {value ? "變更圖示" : "上傳圖示"}
                                </Button>
                            )}
                        </FileButton>
                        {value && (
                            <Button
                                variant="outline"
                                color="red"
                                size="sm"
                                onClick={handleDelete}
                                loading={deleteMutation.isPending}
                                disabled={uploadMutation.isPending}
                            >
                                清除圖示
                            </Button>
                        )}
                    </Group>
                </Stack>
            </Group>
        </Stack>
    );
}

interface WebBotBackgroundImageUploadProps {
    workspaceId: string;
    botProviderId: string;
}

export function WebBotBackgroundImageUpload({
    workspaceId,
    botProviderId
}: WebBotBackgroundImageUploadProps) {
    const queryClient = useQueryClient();
    const botProviderQuery = useGetApiBotProviderGet({
        workspaceId,
        id: botProviderId
    });
    const botProvider = botProviderQuery.data?.data;
    const value = botProvider?.metadata?.[BOT_PROVIDER_METADATA_KEYS.backgroundImageName];

    const uploadMutation = usePostApiBotProviderUploadUploadBackgroundImage();
    const deleteMutation = useDeleteApiBotProviderUploadDeleteBackgroundImage();

    const handleUpload = async (file: File | null) => {
        if (!file) return;

        try {
            await uploadMutation.mutateAsync({
                params: {
                    workspaceId,
                    botProviderId,
                },
                data: {
                    file: file,
                }
            });

            queryClient.invalidateQueries({
                queryKey: botProviderQuery.queryKey
            });
            notifications.show({
                title: "上傳成功",
                message: "背景圖片已更新",
                color: "teal",
            });
        } catch (error) {
            notifications.show({
                title: "上傳失敗",
                message: "無法上傳背景圖片，請稍後再試",
                color: "red",
            });
        }
    };

    const handleDelete = () => {
        modals.openConfirmModal({
            title: "確認清除背景圖片",
            centered: true,
            children: (
                <Text size="sm">
                    您確定要清除目前的背景圖片嗎？此動作無法復原。
                </Text>
            ),
            labels: { confirm: "確認清除", cancel: "取消" },
            confirmProps: { color: "red" },
            onConfirm: async () => {
                try {
                    await deleteMutation.mutateAsync({
                        params: {
                            workspaceId,
                            botProviderId,
                        }
                    });
                    queryClient.invalidateQueries({
                        queryKey: botProviderQuery.queryKey
                    });
                    notifications.show({
                        title: "刪除成功",
                        message: "背景圖片已清除",
                        color: "teal",
                    });
                } catch (error) {
                    notifications.show({
                        title: "刪除失敗",
                        message: "無法清除背景圖片，請稍後再試",
                        color: "red",
                    });
                }
            }
        });
    };

    const imageUrl = value ? GetWebBotBackgroundImageUrl(botProviderId) : undefined;

    return (
        <Stack gap="xs">
            <div>
                <Text size="sm" fw={500}>背景圖片</Text>
                <Text size="xs" c="dimmed">上傳聊天視窗或訊息區域的背景圖案</Text>
            </div>
            <Group align="center" gap="md" wrap="nowrap">
                <Box
                    style={{
                        width: 80,
                        height: 80,
                        minWidth: 80,
                        flexShrink: 0,
                        borderRadius: 8,
                        border: '1px solid var(--mantine-color-gray-3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        backgroundColor: 'var(--mantine-color-gray-0)',
                    }}
                >
                    {imageUrl ? (
                        <Image
                            key={value}
                            src={imageUrl}
                            w={80}
                            h={80}
                            fit="cover"
                            alt="Background Preview"
                        />
                    ) : (
                        <IconPhoto size={36} color="gray" />
                    )}
                </Box>
                <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                    <Group gap="xs" wrap="wrap">
                        <FileButton
                            accept="image/*"
                            onChange={handleUpload}
                            disabled={uploadMutation.isPending}
                        >
                            {(props) => (
                                <Button
                                    {...props}
                                    variant="light"
                                    size="sm"
                                    loading={uploadMutation.isPending}
                                >
                                    {value ? "變更圖片" : "上傳圖片"}
                                </Button>
                            )}
                        </FileButton>
                        {value && (
                            <Button
                                variant="outline"
                                color="red"
                                size="sm"
                                onClick={handleDelete}
                                loading={deleteMutation.isPending}
                                disabled={uploadMutation.isPending}
                            >
                                清除圖片
                            </Button>
                        )}
                    </Group>
                </Stack>
            </Group>
        </Stack>
    );
}